import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, CalendarClock, Clock, User, FileText } from "lucide-react";
import axios from "axios";
import { formatApiError, fmtTime } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Formatage automatique : 6 12 34 56 78
function formatFrenchPhone(val) {
  let digits = val.replace(/\D/g, "");
  if (digits.startsWith("0")) {
    digits = digits.substring(1);
  }
  digits = digits.slice(0, 9); // 9 chiffres après +33

  if (digits.length === 0) return "";
  if (digits.length === 1) return digits;

  const firstDigit = digits[0];
  const rest = digits.slice(1);
  const pairs = rest.match(/.{1,2}/g) || [];

  return `${firstDigit} ${pairs.join(" ")}`;
}

const DAY_LABELS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
const MONTH_NAMES = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"
];

const STEPS = [
  { id: 1, title: "Service", testid: "booking-step-1-service" },
  { id: 2, title: "Professionnel", testid: "booking-step-2-employee" },
  { id: 3, title: "Date", testid: "booking-step-3-date" },
  { id: 4, title: "Créneau", testid: "booking-step-4-slot" },
  { id: 5, title: "Coordonnées", testid: "booking-step-5-client" },
  { id: 6, title: "Confirmation", testid: "booking-step-6-confirm" },
];

export default function PublicBooking() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    axios.get(`${baseURL}/public/tenants/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Cette page de réservation est introuvable ou désactivée."));
  }, [slug]);

  const tz = data?.tenant?.timezone || "Europe/Paris";
  const brand = data?.page?.brand_color || "#2563EB";

  const candidateEmployees = useMemo(() => {
    if (!data || !service) return [];
    if (service.assignment === "specific" && service.employee_ids?.length) {
      return data.employees.filter((e) => service.employee_ids.includes(e.id));
    }
    return data.employees;
  }, [data, service]);

  const showEmployeeStep = !!data?.page?.show_employee && candidateEmployees.length > 1;
  const maxAdvanceDays = Number(data?.settings?.max_advance_days ?? data?.max_advance_days ?? 60);

  const goNext = () => {
    if (step === 1 && !showEmployeeStep) setStep(3);
    else setStep(step + 1);
  };
  const goBack = () => {
    if (step === 3 && !showEmployeeStep) setStep(1);
    else setStep(Math.max(1, step - 1));
  };

  const confirm = async () => {
    const rawPhoneDigits = form.phone.replace(/\D/g, "");
    if (rawPhoneDigits.length < 9) {
      toast.error("Veuillez saisir un numéro de mobile valide (9 chiffres après le +33).");
      setStep(5);
      return;
    }

    const fullPhoneE164 = `+33${rawPhoneDigits}`;
    setSubmitting(true);

    try {
      const { data: res } = await axios.post(`${baseURL}/public/tenants/${slug}/bookings`, {
        service_id: service.id,
        start: slot.start,
        employee_id: employeeId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: fullPhoneE164,
        email: form.email?.trim() || null,
      });
      setConfirmed(res.booking);
      setStep(6);
    } catch (e) {
      toast.error(formatApiError(e, "Réservation impossible"));
      if (e?.response?.status === 409) {
        setSlot(null);
        setStep(4);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <p className="text-center text-sm text-slate-500" data-testid="booking-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ "--brand": brand }}>
      <div className="mx-auto max-w-xl px-4 pb-32 pt-4 sm:pt-10">
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-xl sm:p-8" data-testid="booking-page">
          <div className="mb-6 text-center">
            {data.page.logo_url ? (
              <img src={data.page.logo_url} alt={data.tenant.name} className="mx-auto mb-3 h-14 w-14 rounded-xl object-cover" data-testid="booking-logo" />
            ) : (
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-xl text-white" style={{ backgroundColor: brand }}>
                <CalendarClock className="h-7 w-7" />
              </div>
            )}
            <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="booking-tenant-name">{data.tenant.name}</h1>
            {data.page.show_description && data.tenant.description && (
              <p className="mt-1 text-sm text-slate-500" data-testid="booking-description">{data.tenant.description}</p>
            )}
            {data.page.welcome_message && step === 1 && (
              <p className="mt-2 text-sm font-medium" style={{ color: brand }} data-testid="booking-welcome">{data.page.welcome_message}</p>
            )}

            {/* Encart vers la demande de devis personnalisé */}
            {step === 1 && (
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-blue-100 bg-blue-50/70 p-3 text-left">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                  <p className="text-xs text-blue-900">
                    Besoin d'une estimation préalable ou d'un projet sur-mesure ?
                  </p>
                </div>
                <Link 
                  to={`/quote/${slug}`} 
                  className="inline-flex items-center text-xs font-semibold text-blue-700 hover:text-blue-800 hover:underline shrink-0"
                >
                  Demander un devis →
                </Link>
              </div>
            )}
          </div>

          {!confirmed && (
            <div className="mb-6 flex items-center gap-1" data-testid="booking-progress">
              {STEPS.map((s) => (
                <div key={s.id} className="h-1.5 flex-1 rounded-full transition-colors" style={{ backgroundColor: step >= s.id ? brand : "#E2E8F0" }} data-testid={s.testid} />
              ))}
            </div>
          )}

          {step === 1 && (
            <StepService services={data.services} showPrice={data.page.show_price} selected={service} onSelect={(s) => { setService(s); setSlot(null); setDate(null); }} brand={brand} />
          )}
          {step === 2 && showEmployeeStep && (
            <StepEmployee employees={candidateEmployees} selected={employeeId} onSelect={(id) => { setEmployeeId(id); setSlot(null); setDate(null); }} brand={brand} />
          )}
          {step === 3 && (
            <StepDate slug={slug} baseURL={baseURL} service={service} employeeId={employeeId} selected={date} onSelect={(d) => { setDate(d); setSlot(null); }} maxAdvance={maxAdvanceDays} brand={brand} />
          )}
          {step === 4 && (
            <StepSlots slug={slug} baseURL={baseURL} service={service} employeeId={employeeId} date={date} selected={slot} onSelect={setSlot} tz={tz} brand={brand} />
          )}
          {step === 5 && (
            <StepForm form={form} onChange={setForm} />
          )}
          {step === 6 && confirmed && (
            <StepSuccess booking={confirmed} tenant={data.tenant} tz={tz} brand={brand} />
          )}
          {step === 6 && !confirmed && (
            <StepSummary service={service} employeeName={employeeId ? candidateEmployees.find((e) => e.id === employeeId)?.name : "Premier disponible"} date={date} slot={slot} form={form} tz={tz} showPrice={data.page.show_price} />
          )}

          {!confirmed && (
            <div className="fixed inset-x-0 bottom-0 z-10 border-t border-slate-100 bg-white/90 p-4 backdrop-blur-md sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:pt-6">
              <div className="mx-auto flex max-w-xl gap-3">
                {step > 1 && (
                  <button onClick={goBack} className="flex h-12 items-center justify-center rounded-xl border border-slate-200 px-5 text-sm font-medium text-slate-600" data-testid="btn-booking-back">
                    <ChevronLeft className="mr-1 h-4 w-4" /> Retour
                  </button>
                )}
                {step < 5 && (
                  <button
                    onClick={goNext}
                    disabled={(step === 1 && !service) || (step === 3 && !date) || (step === 4 && !slot)}
                    className="h-12 flex-1 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: brand }}
                    data-testid="btn-booking-next"
                  >
                    Continuer
                  </button>
                )}
                {step === 5 && (
                  <button
                    onClick={() => {
                      const raw = form.phone.replace(/\D/g, "");
                      if (raw.length < 9) {
                        toast.error("Veuillez renseigner un numéro de téléphone valide.");
                        return;
                      }
                      setStep(6);
                    }}
                    disabled={!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()}
                    className="h-12 flex-1 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: brand }}
                    data-testid="btn-booking-review"
                  >
                    Voir le récapitulatif
                  </button>
                )}
                {step === 6 && !confirmed && (
                  <button
                    onClick={confirm}
                    disabled={submitting}
                    className="flex h-12 flex-1 items-center justify-center rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: brand }}
                    data-testid="btn-confirm-booking"
                  >
                    {submitting ? "Confirmation..." : "Confirmer le rendez-vous"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">Propulsé par RendezVous</p>
      </div>
    </div>
  );
}

function StepService({ services, showPrice, selected, onSelect, brand }) {
  return (
    <div className="space-y-3" data-testid="step-service-content">
      <h2 className="font-display text-lg font-semibold text-slate-900">Choisissez votre prestation</h2>
      {services.length === 0 && <p className="text-sm text-slate-400">Aucune prestation disponible pour le moment.</p>}
      {services.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect(s)}
          data-testid={`service-option-${s.id}`}
          className={`flex min-h-[56px] w-full items-center justify-between gap-3 rounded-xl border-2 p-4 text-left transition-colors ${
            selected?.id === s.id ? "border-[var(--brand)] bg-slate-50" : "border-slate-100 hover:border-slate-300"
          }`}
          style={selected?.id === s.id ? { borderColor: brand } : {}}
        >
          <div>
            <p className="font-medium text-slate-900">{s.name}</p>
            {s.description && <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{s.description}</p>}
          </div>
          <div className="shrink-0 text-right">
            <p className="flex items-center gap-1 text-sm font-medium text-slate-700"><Clock className="h-3.5 w-3.5" /> {s.duration_min} min</p>
            {showPrice && s.price != null && <p className="mt-0.5 text-sm font-semibold" style={{ color: brand }}>{s.price} €</p>}
          </div>
        </button>
      ))}
    </div>
  );
}

function StepEmployee({ employees, selected, onSelect, brand }) {
  return (
    <div className="space-y-3" data-testid="step-employee-content">
      <h2 className="font-display text-lg font-semibold text-slate-900">Choisissez un professionnel</h2>
      <button
        onClick={() => onSelect(null)}
        data-testid="employee-option-any"
        className="flex min-h-[56px] w-full items-center gap-3 rounded-xl border-2 border-slate-100 p-4 text-left hover:border-slate-300"
        style={selected === null ? { borderColor: brand } : {}}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
          <User className="h-5 w-5 text-slate-500" />
        </div>
        <div>
          <p className="font-medium text-slate-900">Premier disponible</p>
          <p className="text-xs text-slate-500">Le créneau le plus tôt possible</p>
        </div>
      </button>
      {employees.map((e) => (
        <button
          key={e.id}
          onClick={() => onSelect(e.id)}
          data-testid={`employee-option-${e.id}`}
          className="flex min-h-[56px] w-full items-center gap-3 rounded-xl border-2 border-slate-100 p-4 text-left hover:border-slate-300"
          style={selected === e.id ? { borderColor: brand } : {}}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-bold text-white" style={{ backgroundColor: brand }}>
            {e.name.slice(0, 2).toUpperCase()}
          </div>
          <p className="font-medium text-slate-900">{e.name}</p>
        </button>
      ))}
    </div>
  );
}

function StepDate({ slug, baseURL, service, employeeId, selected, onSelect, maxAdvance = 60, brand }) {
  const [month, setMonth] = useState(() => new Date());
  const [availableDays, setAvailableDays] = useState(null);

  const advDays = Number(maxAdvance) || 60;

  useEffect(() => {
    const params = { service_id: service.id, days: advDays };
    if (employeeId) params.employee_id = employeeId;
    axios.get(`${baseURL}/public/tenants/${slug}/available-days`, { params })
      .then((r) => setAvailableDays(new Set(r.data.days)))
      .catch(() => setAvailableDays(new Set()));
  }, [service, employeeId, slug, baseURL, advDays]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + advDays);
  maxDate.setHours(23, 59, 59, 999);

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();

  const canPrev = new Date(month.getFullYear(), month.getMonth(), 1) > new Date(today.getFullYear(), today.getMonth(), 1);
  const canNext = true;

  return (
    <div data-testid="step-date-content">
      <h2 className="font-display text-lg font-semibold text-slate-900">Choisissez une date</h2>
      <div className="mt-4 flex items-center justify-between">
        <button 
          type="button"
          onClick={() => canPrev && setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} 
          disabled={!canPrev} 
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30" 
          data-testid="date-prev-month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <p className="font-display text-base font-semibold capitalize text-slate-900" data-testid="date-month-label">
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </p>
        <button 
          type="button"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} 
          disabled={!canNext} 
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30" 
          data-testid="date-next-month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1 text-center">
        {DAY_LABELS.map((d) => (
          <p key={d} className="py-1 text-xs font-semibold text-slate-400">{d}</p>
        ))}
        {Array.from({ length: startOffset }).map((_, i) => <span key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const d = new Date(month.getFullYear(), month.getMonth(), i + 1);
          d.setHours(0, 0, 0, 0);

          const k = key(d);
          const isPast = d < today;
          const isTooFar = d > maxDate;
          const isWithinRange = !isPast && !isTooFar;

          const available = isWithinRange && (availableDays ? availableDays.has(k) : false);
          const isSelected = selected === k;

          return (
            <button
              key={k}
              type="button"
              disabled={!available}
              onClick={() => onSelect(k)}
              data-testid={`date-day-${k}`}
              className={`flex h-11 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                isSelected 
                  ? "text-white" 
                  : available 
                    ? "text-slate-900 hover:bg-slate-100" 
                    : "cursor-not-allowed text-slate-300"
              }`}
              style={isSelected ? { backgroundColor: brand } : available ? { boxShadow: `inset 0 0 0 1.5px ${brand}40` } : {}}
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      {availableDays && availableDays.size === 0 && (
        <p className="mt-4 text-center text-sm text-slate-400" data-testid="no-available-days">
          Aucune disponibilité dans la période sélectionnée.
        </p>
      )}
      {availableDays === null && (
        <p className="mt-4 text-center text-sm text-slate-400">Chargement des disponibilités...</p>
      )}
    </div>
  );
}

function StepSlots({ slug, baseURL, service, employeeId, date, selected, onSelect, tz, brand }) {
  const [slots, setSlots] = useState(null);

  useEffect(() => {
    const params = { service_id: service.id, date };
    if (employeeId) params.employee_id = employeeId;
    axios.get(`${baseURL}/public/tenants/${slug}/slots`, { params })
      .then((r) => setSlots(r.data))
      .catch(() => setSlots([]));
  }, [date, service, employeeId, slug, baseURL]);

  return (
    <div data-testid="step-slot-content">
      <h2 className="font-display text-lg font-semibold text-slate-900">Choisissez un créneau</h2>
      <p className="mt-1 text-sm capitalize text-slate-500" data-testid="slot-date-label">
        {new Date(date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
      </p>
      {slots === null ? (
        <p className="mt-6 text-center text-sm text-slate-400">Chargement des créneaux...</p>
      ) : slots.length === 0 ? (
        <p className="mt-6 text-center text-sm text-slate-400" data-testid="no-slots">Aucun créneau disponible ce jour.</p>
      ) : (
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="slots-grid">
          {slots.map((s) => (
            <button
              key={s.start}
              onClick={() => onSelect(s)}
              data-testid={`slot-option-${fmtTime(s.start, tz)}`}
              className={`flex h-12 items-center justify-center rounded-xl border-2 text-sm font-semibold transition-colors ${
                selected?.start === s.start ? "text-white" : "border-slate-100 text-slate-800 hover:border-slate-300"
              }`}
              style={selected?.start === s.start ? { backgroundColor: brand, borderColor: brand } : {}}
            >
              {fmtTime(s.start, tz)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StepForm({ form, onChange }) {
  return (
    <div className="space-y-4" data-testid="step-form-content">
      <h2 className="font-display text-lg font-semibold text-slate-900">Vos coordonnées</h2>

      <div>
        <label className="text-sm font-medium text-slate-700" htmlFor="first_name">
          Prénom *
        </label>
        <input
          id="first_name"
          type="text"
          value={form.first_name}
          onChange={(e) => onChange({ ...form, first_name: e.target.value })}
          placeholder="Jean"
          required
          className="mt-1 h-12 w-full rounded-xl border border-slate-200 px-4 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          data-testid="client-first_name"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700" htmlFor="last_name">
          Nom *
        </label>
        <input
          id="last_name"
          type="text"
          value={form.last_name}
          onChange={(e) => onChange({ ...form, last_name: e.target.value })}
          placeholder="Dupont"
          required
          className="mt-1 h-12 w-full rounded-xl border border-slate-200 px-4 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          data-testid="client-last_name"
        />
      </div>

      {/* Champ Mobile avec badge fixe 🇫🇷 +33 et espacement naturel */}
      <div>
        <label className="text-sm font-medium text-slate-700" htmlFor="phone">
          Numéro de téléphone mobile *
        </label>
        <div className="relative mt-1 flex h-12 items-stretch rounded-xl border border-slate-200 overflow-hidden focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 bg-white">
          <span className="inline-flex items-center justify-center border-r border-slate-200 bg-slate-100 px-3.5 text-sm font-semibold text-slate-700 select-none">
            🇫🇷 +33
          </span>
          <input
            id="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => {
              const formatted = formatFrenchPhone(e.target.value);
              onChange({ ...form, phone: formatted });
            }}
            placeholder="6 12 34 56 78"
            required
            className="h-full flex-1 px-4 text-base font-medium outline-none bg-transparent"
            data-testid="client-phone"
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700" htmlFor="email">
          Email <span className="ml-1 text-xs text-slate-400">(facultatif)</span>
        </label>
        <input
          id="email"
          type="email"
          value={form.email}
          onChange={(e) => onChange({ ...form, email: e.target.value })}
          placeholder="jean.dupont@exemple.com"
          className="mt-1 h-12 w-full rounded-xl border border-slate-200 px-4 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          data-testid="client-email"
        />
      </div>
    </div>
  );
}

function StepSummary({ service, employeeName, date, slot, form, tz, showPrice }) {
  return (
    <div data-testid="step-summary-content">
      <h2 className="font-display text-lg font-semibold text-slate-900">Récapitulatif</h2>
      <div className="mt-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm">
        <Row label="Prestation" value={`${service.name}${showPrice && service.price != null ? ` — ${service.price} €` : ""}`} />
        <Row label="Professionnel" value={employeeName || "Premier disponible"} />
        <Row label="Date" value={new Date(date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} />
        <Row label="Heure" value={`${fmtTime(slot.start, tz)} (${service.duration_min} min)`} />
        <Row label="Client" value={`${form.first_name} ${form.last_name}`} />
        <Row label="Téléphone" value={`+33 ${form.phone}`} />
        {form.email && <Row label="Email" value={form.email} />}
      </div>
    </div>
  );
}

function StepSuccess({ booking, tenant, tz, brand }) {
  return (
    <div className="py-6 text-center" data-testid="step-success-content">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: `${brand}1A` }}>
        <Check className="h-8 w-8" style={{ color: brand }} />
      </div>
      <h2 className="mt-4 font-display text-2xl font-bold text-slate-900">Rendez-vous confirmé !</h2>
      <p className="mt-2 text-sm text-slate-500">
        {tenant.name} vous attend le{" "}
        <span className="font-semibold text-slate-900">
          {new Date(booking.start_at).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: tz })}
        </span>{" "}
        à <span className="font-semibold text-slate-900">{fmtTime(booking.start_at, tz)}</span>
      </p>
      <div className="mx-auto mt-6 max-w-xs space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-4 text-left text-sm">
        <Row label="Prestation" value={booking.service_name} />
        <Row label="Avec" value={booking.employee_name} />
        <Row label="Horaire" value={`${fmtTime(booking.start_at, tz)} → ${fmtTime(booking.end_at, tz)}`} />
      </div>
      <p className="mt-6 text-xs text-slate-400">Vous recevrez un SMS de rappel avant votre rendez-vous.</p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium capitalize text-slate-900">{value}</span>
    </div>
  );
}

function key(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}